import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import SubHeader from "components/common/SubHeader";
import Badge from "components/common/Badge";
import TuiViewer from "components/common/TuiViewer";
import { getNoticeDetail } from "api/notice";
import { useSnackbar } from "contexts/SnackbarContext";
import { renderWithLinks } from "utils/renderWithLinks";
import "assets/scss/pages/noticeDetail.scss";

// published_at(ISO) → YYYY-MM-DD
const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    if (isNaN(date.getTime())) return "";
    const yyyy = date.getFullYear();
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const dd = String(date.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
};

// 에디터(Quill) HTML 본문인지 판별한다. 기존 plain text 공지는 renderWithLinks 로 유지.
// `<https://...>` 같은 꺾쇠 URL 을 태그로 오인하지 않도록 에디터가 쓰는 태그만 본다.
const HTML_TAG_REGEX = /<(p|br|div|span|h[1-6]|ul|ol|li|img|a|strong|em|u|s|blockquote|pre)(\s[^>]*)?\/?>/i;
const isHtmlContent = (content) => !!content && HTML_TAG_REGEX.test(content);

const NoticeDetail = () => {
    const { noticeId } = useParams();
    const [notice, setNotice] = useState(null);
    const [loading, setLoading] = useState(true);
    const { showError } = useSnackbar();

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                setLoading(true);
                const data = await getNoticeDetail(noticeId);
                if (active) setNotice(data);
            } catch {
                if (active) showError("공지사항을 불러오지 못했습니다");
            } finally {
                if (active) setLoading(false);
            }
        })();
        return () => {
            active = false;
        };
    }, [noticeId, showError]);

    return (
        <div className="page-notice-detail">
            <SubHeader title="공지사항" />

            <div className="notice-detail-content">
                {loading ? (
                    <p className="notice-detail__loading">불러오는 중...</p>
                ) : !notice ? (
                    <p className="notice-detail__empty">공지사항을 찾을 수 없습니다</p>
                ) : (
                    <>
                        <div className="notice-detail__head">
                            {notice.is_important && (
                                <Badge text="중요" type="soft" color="red" />
                            )}
                            <h2 className="notice-detail__title">{notice.title}</h2>
                            <span className="notice-detail__date">
                                {formatDate(notice.published_at)}
                            </span>
                        </div>
                        {isHtmlContent(notice.content) ? (
                            <TuiViewer
                                htmlString={notice.content}
                                className="notice-detail__body notice-detail__body--html"
                            />
                        ) : (
                            <p className="notice-detail__body">{renderWithLinks(notice.content, "notice-detail__link")}</p>
                        )}
                    </>
                )}
            </div>
        </div>
    );
};

export default NoticeDetail;
